// imports
import { FC, memo, useCallback, useMemo, useState } from 'react';
// components
import InfiniteSelect from './InfiniteSelect';
// interfaces, constants, graphql
import { HTTP_STATUS } from 'constants/index';
import { ExcipientLabelSelectProps, SelectType } from 'interfaces';
import { ExcipientLabelsPayload, useFindAllExcipientLabelSelectLazyQuery } from 'generated/graphql';

const ExcipientLabelSelect: FC<ExcipientLabelSelectProps> = ({
  name,
  title,
  value,
  isActive,
  handleChange,
  margin = 'dense',
  isClearable = false,
  ...rest
}) => {
  const [totalPages, setTotalPages] = useState(0);
  const [getLoading, setGetLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<ExcipientLabelsPayload['data']>([]);

  const updatedOptions = useMemo(() => {
    if (options?.length) {
      return options?.map<SelectType>((item) => {
        const { id, name, isActive } = item || {};

        return {
          value: id,
          name: name || '',
          isActive,
        };
      });
    }
    return [];
  }, [options]);

  const [fetchSelectorData, { loading }] = useFindAllExcipientLabelSelectLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { findAllExcipientLabels } = data;
      const { data: excipientLabels, response, pagination } = findAllExcipientLabels || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        const { totalPages: pages } = pagination || {};
        setTotalPages(pages);
        setOptions([
          ...(getLoading ? [] : options || []),
          ...((excipientLabels as ExcipientLabelsPayload['data']) ?? []),
        ]);
        setGetLoading(false);
      } else {
        setGetLoading(false);
      }
    },
    onError: () => {
      setOptions([]);
      setGetLoading(false);
    },
  });

  const fetchData = useCallback(
    async (page?: number, query?: string) => {
      if (!page) setGetLoading(true);
      await fetchSelectorData({
        variables: {
          findAllExcipientLabelsInput: {
            ...(query && { search: query }),
            paginationOptions: { page: page || 1, limit: 10 },
            ...(typeof isActive === 'boolean' && { isActive }),
          },
        },
      });
    },
    [fetchSelectorData, isActive],
  );

  return (
    <InfiniteSelect
      {...rest}
      name={name}
      title={title}
      value={value}
      margin={margin}
      loading={loading}
      fetchData={fetchData}
      setOptions={setOptions}
      totalPages={totalPages}
      getLoading={getLoading}
      options={updatedOptions}
      isClearable={isClearable}
      handleChange={handleChange}
    />
  );
};

export default memo(ExcipientLabelSelect);
